/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import type {Application, Metric, MetricsApi, User} from '@exc/metrics';
import {BUILD_VERSION} from './BuildVersion';
import {INIT_FLUSH_PERIOD} from './MetricsProcessing';

const CONSOLE_NORMAL = {
  debug: false,
  error: true,
  info: false,
  metric: false,
  trace: false,
  warn: true
};
const CONSOLE_VERBOSE = {
  debug: true,
  error: true,
  info: true,
  metric: false,
  trace: true,
  warn: true
};
const CONSOLE_VVV = {
  debug: true,
  error: true,
  info: true,
  metric: true,
  trace: true,
  warn: true
};
const CONSOLE_OFF = {
  debug: false,
  error: false,
  info: false,
  metric: false,
  trace: false,
  warn: false
};

/**
 * Initialize an url on the adobeMetrics object for deep-linking to ADX.
 *
 * @param {AdobeMetricsRuntime} adobeMetrics Adobe Metrics object.
 */
function initQueryLink(adobeMetrics: AdobeMetricsRuntime): void {
  const {metricsState: {environment, instanceId}} = adobeMetrics;
  const query = `Log | where InstanceId == "${instanceId}" | order by TimeStamp asc`;
  const encodedQueryString = encodeURI(query);
  const cluster = environment === 'prod' ? 'prodmetricsfollower1' : environment + 'metrics';
  adobeMetrics.queryURL = `https://dataexplorer.azure.com/clusters/${cluster}.eastus2/databases/Metrics?query=${encodedQueryString}`;
}

export interface ConfigMethods {
  clearUser(version?: string): void;
  setApplication(app: Application): void;
  setEnvironment(env?: string): void;
  setMode(mode: string): void;
  setUser(user: User, version?: string): void;
}

export default (adobeMetrics: AdobeMetricsRuntime, metrics: MetricsApi): ConfigMethods => {
  /**
   * Validate and set the runtime environment to 'dev' | 'stage' | 'prod' based
   * on an input environment which will be case-insensitive string-matched. If
   * env is not stage | prod, then it defaults to dev.
   *
   * @param {string} env The string representing the environment.
   */
  function setEnvironment(env?: string): void {
    if (!env) {return;}
    env = env && env.toLowerCase();
    switch (env) {
      case 'prod':
      case 'stage':
        break;
      default:
        env = 'dev';
    }
    adobeMetrics.metricsState.environment = env;
    // add adx deep link for client to access ADX without copy and paste
    initQueryLink(adobeMetrics);
  }
  /**
   * Validate and set the runtime mode to
   * 'normal' | 'verbose' | 'quiet' | 'test' | 'off'.
   *
   * @param {string} mode The mode of metrics operation, default to 'normal'.
   */
  function setMode(mode: string): void {
    mode = mode && mode.toLowerCase();

    // make sure mode is one of the supported values
    switch (mode) {
      case 'verbose':
      case 'vvv':
      case 'quiet':
      case 'test':
      case 'off':
        break;
      default:
        mode = 'normal';
    }

    const runtime = adobeMetrics.runtime;
    runtime.mode = mode;
    switch (mode) {
      case 'verbose':
        Object.assign(runtime.options, {
          console: CONSOLE_VERBOSE,
          timestamp: true
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: 1000,
          transmit: true
        });
        break;
      case 'vvv':
        Object.assign(runtime.options, {
          console: CONSOLE_VVV,
          timestamp: true
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: 1000,
          transmit: true
        });
        break;
      case 'quiet':
        Object.assign(runtime.options, {
          console: CONSOLE_OFF,
          timestamp: false
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: INIT_FLUSH_PERIOD,
          transmit: true
        });
        break;
      case 'test':
        Object.assign(runtime.options, {
          console: CONSOLE_VERBOSE,
          timestamp: true
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: 0,
          transmit: false
        });
        break;
      case 'off':
        Object.assign(runtime.options, {
          console: CONSOLE_OFF,
          timestamp: false
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: 0,
          transmit: false
        });
        break;
      default:
        Object.assign(runtime.options, {
          console: CONSOLE_NORMAL,
          timestamp: false
        });
        Object.assign(runtime.options.transport, {
          flushPeriod: INIT_FLUSH_PERIOD,
          transmit: true
        });
        break;
    }
  }
  /**
   * Record current User Properties for including in Metrics records.
   * Only add new user to Payload user array but always set Metrics.user.
   * Supplied properties (except for event) will be merged into the existing
   * user object, which allows for updating some properties such as the active
   * group, without losing other state previously provided. The event property
   * from the user parameter will be recorded as-is, including if undefined.
   *
   * @param {User} user The user properties to record.
   * @param {string} version The Metrics sdk version that created this user.
   */
  function setUser(user: User, version?: string): void {
    const m: Metric = Object.assign({}, {metricsState: {sdkVersion: version || BUILD_VERSION}, user});
    metrics.event('exc.metrics.setUser', m);
  }
  /**
   * Clear User profile and group, typically called upon logout or a detected
   * timeout. This method will reset Metrics.user back to an anonymous user.
   *
   * @param {string} version The Metrics sdk version that cleared this user.
   */
  function clearUser(version?: string): void {
    const m: Metric = Object.assign({}, {metricsState: {sdkVersion: version || BUILD_VERSION}});
    metrics.event('exc.metrics.clearUser', m);
  }
  /**
   * Set the application context information into the environment,
   * so that it can be recorded with each metric.
   *
   * @param {Application} app The application information,
   * conforming to the Metric.application property bag. Solution
   * or sub-application can be merged in by passing just that portion
   * of the object, such as {solution: {id: 'solution2'}}.
   *
   * To delete application information, pass undefined.
   */
  function setApplication(app: Application): void {
    metrics.event('exc.metrics.setApplication', app);
  }

  return {
    clearUser,
    setApplication,
    setEnvironment,
    setMode,
    setUser
  };
};
